import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lock, Award, Star, Send, CheckCircle, Clock, Github, Globe, Video, Image,
  LogOut, Brain, ChevronDown, ChevronUp, ExternalLink, AlertCircle, Trophy,
  FileText, Play, Loader, RefreshCw, Users, Mail, Phone, Building, Code,
  Target, Layers, Eye, MessageSquare, Calendar, ArrowRight, X
} from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Score Slider Component
const ScoreSlider = ({ label, value, onChange, description, weight }) => (
  <div className="mb-4">
    <div className="flex justify-between items-center mb-2">
      <div>
        <label className="text-sm font-semibold text-gray-700">{label}</label>
        {weight && <span className="text-xs text-gray-500 ml-2">({weight})</span>}
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
      <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold">{value}</span>
    </div>
    <input
      type="range"
      min="1"
      max="10"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
    />
    <div className="flex justify-between text-xs text-gray-400 mt-1">
      <span>Poor (1)</span>
      <span>Excellent (10)</span>
    </div>
  </div>
);

// AI Score Display Component
const AIScoreCard = ({ aiEvaluation, onUseScores }) => {
  if (!aiEvaluation?.scores) return null;
  
  const scores = aiEvaluation.scores;
  const totalScore = scores.total_score || (
    (scores.innovation * 0.2 + scores.technical_implementation * 0.25 + 
     scores.problem_relevance * 0.25 + scores.scalability_impact * 0.15 + 
     scores.presentation_quality * 0.15) * 10
  ).toFixed(1);

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-blue-800 flex items-center gap-2">
          <Brain size={20} />
          AI Evaluation Report
        </h4>
        <span className="text-xs text-blue-600">
          {aiEvaluation.evaluated_at ? new Date(aiEvaluation.evaluated_at).toLocaleDateString() : 'N/A'}
        </span>
      </div>
      
      {/* Score Grid */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        {[
          { key: 'innovation', label: 'Innovation', weight: '20%' },
          { key: 'technical_implementation', label: 'Technical', weight: '25%' },
          { key: 'problem_relevance', label: 'Relevance', weight: '25%' },
          { key: 'scalability_impact', label: 'Impact', weight: '15%' },
          { key: 'presentation_quality', label: 'Presentation', weight: '15%' }
        ].map(({ key, label, weight }) => (
          <div key={key} className="bg-white rounded-lg p-3 text-center shadow-sm">
            <div className="text-2xl font-bold text-blue-700">{scores[key] || 'N/A'}</div>
            <div className="text-xs text-gray-600">{label}</div>
            <div className="text-xs text-gray-400">{weight}</div>
          </div>
        ))}
      </div>

      {/* Total Score */}
      <div className="bg-white rounded-lg p-4 flex items-center justify-between mb-4">
        <span className="font-medium text-gray-700">Weighted Total Score</span>
        <span className="text-3xl font-bold text-blue-600">{totalScore}/100</span>
      </div>

      {/* Strengths & Weaknesses */}
      {(scores.strengths?.length > 0 || scores.weaknesses?.length > 0) && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          {scores.strengths?.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-green-700 mb-2">Strengths</h5>
              <ul className="space-y-1">
                {scores.strengths.map((s, i) => (
                  <li key={i} className="text-xs text-green-600 flex items-start gap-1">
                    <CheckCircle size={12} className="mt-0.5 flex-shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {scores.weaknesses?.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-orange-700 mb-2">Areas for Improvement</h5>
              <ul className="space-y-1">
                {scores.weaknesses.map((w, i) => (
                  <li key={i} className="text-xs text-orange-600 flex items-start gap-1">
                    <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Recommendation */}
      {scores.recommendation && (
        <div className="bg-blue-100 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800 italic">&quot;{scores.recommendation}&quot;</p>
        </div>
      )}

      {/* Shortlist Recommendation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            scores.shortlist_recommendation 
              ? 'bg-green-100 text-green-700' 
              : 'bg-orange-100 text-orange-700'
          }`}>
            {scores.shortlist_recommendation ? '✓ Recommended for Shortlist' : '⚠ Needs Review'}
          </span>
        </div>
        {onUseScores && (
          <button
            onClick={onUseScores}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Use AI scores as starting point
          </button>
        )}
      </div>
    </div>
  );
};

// Team Members Display
const TeamMembersCard = ({ members }) => {
  if (!members || members.length === 0) return null;
  
  return (
    <div className="bg-gray-50 rounded-lg p-4 mb-4">
      <h4 className="font-medium text-gray-800 flex items-center gap-2 mb-3">
        <Users size={18} />
        Team Members ({members.length})
      </h4>
      <div className="grid gap-3">
        {members.map((member, idx) => (
          <div key={idx} className="bg-white rounded-lg p-3 flex items-center gap-3 border">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              member.is_leader ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'
            }`}>
              {member.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{member.name}</span>
                {member.is_leader && (
                  <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">Leader</span>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                <span className="flex items-center gap-1"><Mail size={12} />{member.email}</span>
                {member.phone && <span className="flex items-center gap-1"><Phone size={12} />{member.phone}</span>}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {member.college} • {member.branch} • {member.year}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Round Submission Display
const RoundSubmissionCard = ({ roundNumber, submission, isActive }) => {
  const hasData = submission && Object.keys(submission).some(k => submission[k]);
  
  return (
    <div className={`rounded-lg border p-4 ${
      isActive ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-gray-50'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-medium flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
            isActive ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-600'
          }`}>
            {roundNumber}
          </div>
          Round {roundNumber}
        </h5>
        {hasData && (
          <span className="text-xs text-green-600 flex items-center gap-1">
            <CheckCircle size={12} /> Submitted
          </span>
        )}
      </div>
      
      {hasData ? (
        <div className="space-y-2">
          {roundNumber === 1 && (
            <>
              {submission.github_url && (
                <a href={submission.github_url} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <Github size={14} /> GitHub Repository <ExternalLink size={12} />
                </a>
              )}
              {submission.prototype_url && (
                <a href={submission.prototype_url} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <Globe size={14} /> Live Demo <ExternalLink size={12} />
                </a>
              )}
              {submission.video_drive_url && (
                <a href={submission.video_drive_url} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <Video size={14} /> Project Video <ExternalLink size={12} />
                </a>
              )}
              {submission.images_drive_url && (
                <a href={submission.images_drive_url} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <Image size={14} /> Project Images <ExternalLink size={12} />
                </a>
              )}
              {submission.submitted_at && (
                <p className="text-xs text-gray-500 mt-2">
                  Submitted: {new Date(submission.submitted_at).toLocaleString()}
                </p>
              )}
            </>
          )}
          {roundNumber === 3 && (
            <>
              {submission.pitch_deck_url && (
                <a href={submission.pitch_deck_url} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <FileText size={14} /> Pitch Deck <ExternalLink size={12} />
                </a>
              )}
              {submission.final_demo_url && (
                <a href={submission.final_demo_url} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <Play size={14} /> Final Demo <ExternalLink size={12} />
                </a>
              )}
              {submission.additional_notes && (
                <p className="text-sm text-gray-600 mt-2 bg-white p-2 rounded">
                  {submission.additional_notes}
                </p>
              )}
            </>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No submission yet</p>
      )}
    </div>
  );
};

const JuryDashboardPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [viewingDetails, setViewingDetails] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [aiEvaluating, setAiEvaluating] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [scoreForm, setScoreForm] = useState({
    innovation: 5,
    technical_implementation: 5,
    problem_relevance: 5,
    scalability_impact: 5,
    presentation_quality: 5,
    comments: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('jury_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsLoggedIn(true);
      fetchDashboard();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('username', loginForm.username);
      formData.append('password', loginForm.password);

      const response = await axios.post(`${API}/jury/login`, formData);
      localStorage.setItem('jury_token', response.data.token);
      localStorage.setItem('jury_name', response.data.name);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      setIsLoggedIn(true);
      toast.success(`Welcome, ${response.data.name}!`);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('jury_token');
    localStorage.removeItem('jury_name');
    delete axios.defaults.headers.common['Authorization'];
    setIsLoggedIn(false);
    setDashboard(null);
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/jury/dashboard`);
      setDashboard(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to fetch dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const triggerAiEvaluation = async (submissionId) => {
    setAiEvaluating(true);
    try {
      const response = await axios.post(`${API}/jury/submissions/${submissionId}/ai-evaluate`);
      
      if (response.data.result?.error) {
        toast.error(response.data.result.error);
      } else {
        toast.success('AI evaluation completed');
        
        // Update the submission in dashboard
        fetchDashboard();
        
        // Update viewing details if open
        if (viewingDetails?.id === submissionId) {
          setViewingDetails(prev => ({ ...prev, ai_evaluation: response.data.result }));
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI evaluation failed');
    } finally {
      setAiEvaluating(false);
    }
  };

  const submitScore = async () => {
    if (!selectedSubmission) return;
    
    setScoring(true);
    try {
      await axios.post(`${API}/jury/score-v2/${selectedSubmission.id}`, {
        ...scoreForm,
        round: dashboard?.current_round || 1
      });
      toast.success('Score submitted successfully');
      setSelectedSubmission(null);
      setViewingDetails(null);
      resetScoreForm();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit score');
    } finally {
      setScoring(false);
    }
  };

  const resetScoreForm = () => {
    setScoreForm({
      innovation: 5,
      technical_implementation: 5,
      problem_relevance: 5,
      scalability_impact: 5,
      presentation_quality: 5,
      comments: ''
    });
  };

  const applyAiScores = (aiScores) => {
    if (aiScores) {
      setScoreForm({
        ...scoreForm,
        innovation: aiScores.innovation || 5,
        technical_implementation: aiScores.technical_implementation || 5,
        problem_relevance: aiScores.problem_relevance || 5,
        scalability_impact: aiScores.scalability_impact || 5,
        presentation_quality: aiScores.presentation_quality || 5
      });
    }
  };

  const calculateTotalScore = () => {
    const weights = {
      innovation: 0.20,
      technical_implementation: 0.25,
      problem_relevance: 0.25,
      scalability_impact: 0.15,
      presentation_quality: 0.15
    };
    
    let total = 0;
    for (const [key, weight] of Object.entries(weights)) {
      total += scoreForm[key] * weight;
    }
    return (total * 10).toFixed(1);
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
        <Toaster position="top-right" />
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Award className="text-purple-600" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Jury Dashboard</h1>
            <p className="text-gray-500 mt-2">Login to evaluate submissions</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email / Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your email"
                required
                data-testid="jury-login-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your password"
                required
                data-testid="jury-login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="jury-login-submit"
            >
              {loading ? <Loader className="animate-spin" size={20} /> : <Lock size={20} />}
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Award className="text-purple-600" size={24} />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">Jury Dashboard</h1>
              <p className="text-sm text-gray-500">Welcome, {dashboard?.jury_name || 'Jury Member'}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-purple-100 rounded-full">
              <Trophy className="text-purple-600" size={16} />
              <span className="text-sm font-medium text-purple-700">
                Round {dashboard?.current_round || 1}
              </span>
            </div>
            <button
              onClick={fetchDashboard}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Refresh"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              data-testid="jury-logout-btn"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading && !dashboard ? (
          <div className="flex justify-center py-12">
            <Loader className="animate-spin text-purple-600" size={40} />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 border">
                <div className="text-3xl font-bold text-purple-600">{dashboard?.stats?.total_assigned || 0}</div>
                <div className="text-sm text-gray-500">Total Assigned</div>
              </div>
              <div className="bg-white rounded-xl p-4 border">
                <div className="text-3xl font-bold text-orange-600">{dashboard?.stats?.pending || 0}</div>
                <div className="text-sm text-gray-500">Pending Review</div>
              </div>
              <div className="bg-white rounded-xl p-4 border">
                <div className="text-3xl font-bold text-green-600">{dashboard?.stats?.scored || 0}</div>
                <div className="text-sm text-gray-500">Scored</div>
              </div>
              <div className="bg-white rounded-xl p-4 border">
                <div className="text-3xl font-bold text-blue-600">
                  {dashboard?.current_round || 1}
                </div>
                <div className="text-sm text-gray-500">Current Round</div>
              </div>
            </div>

            {/* Pending Submissions */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="text-orange-600" size={20} />
                Pending Review ({dashboard?.pending_scoring?.length || 0})
              </h2>
              
              {dashboard?.pending_scoring?.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center border">
                  <CheckCircle className="mx-auto text-green-500 mb-3" size={48} />
                  <h3 className="font-medium text-gray-900">All caught up!</h3>
                  <p className="text-gray-500">No pending submissions to review</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {dashboard?.pending_scoring?.map(submission => (
                    <div key={submission.id} className="bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-gray-900 text-lg">{submission.team_name}</h3>
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                {submission.theme}
                              </span>
                            </div>
                            <p className="text-gray-600">{submission.project_title}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {submission.ai_evaluation?.scores && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                                <Brain size={12} />
                                AI: {submission.ai_evaluation.scores.total_score || 'N/A'}
                              </span>
                            )}
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              submission.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                              submission.status === 'round2_shortlisted' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {submission.status}
                            </span>
                          </div>
                        </div>

                        {/* Problem Statement */}
                        {submission.problem_statement?.title && (
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                              <Target size={14} />
                              <span className="font-medium">Problem Statement</span>
                            </div>
                            <p className="text-sm text-gray-800">{submission.problem_statement.title}</p>
                          </div>
                        )}

                        {/* Quick Links */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {submission.round1?.github_url && (
                            <a href={submission.round1.github_url} target="_blank" rel="noopener noreferrer"
                               className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200">
                              <Github size={14} /> GitHub
                            </a>
                          )}
                          {submission.round1?.prototype_url && (
                            <a href={submission.round1.prototype_url} target="_blank" rel="noopener noreferrer"
                               className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200">
                              <Globe size={14} /> Demo
                            </a>
                          )}
                          {submission.round1?.video_drive_url && (
                            <a href={submission.round1.video_drive_url} target="_blank" rel="noopener noreferrer"
                               className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200">
                              <Video size={14} /> Video
                            </a>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => setViewingDetails(submission)}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                            data-testid={`view-details-${submission.id}`}
                          >
                            <Eye size={18} />
                            View Full Details
                          </button>
                          <button
                            onClick={() => triggerAiEvaluation(submission.id)}
                            disabled={aiEvaluating}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            data-testid={`ai-evaluate-${submission.id}`}
                          >
                            {aiEvaluating ? <Loader className="animate-spin" size={18} /> : <Brain size={18} />}
                            {submission.ai_evaluation ? 'Re-run AI' : 'AI Evaluate'}
                          </button>
                          <button
                            onClick={() => {setSelectedSubmission(submission); setViewingDetails(submission);}}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            data-testid={`score-${submission.id}`}
                          >
                            <Star size={18} />
                            Score Now
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Scored Submissions */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                Scored ({dashboard?.scored?.length || 0})
              </h2>
              
              {dashboard?.scored?.length > 0 && (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Team</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Project</th>
                          <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">My Score</th>
                          <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">AI Score</th>
                          <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {dashboard?.scored?.map(submission => {
                          const myScore = submission.my_scores?.[0];
                          const myTotal = myScore ? (
                            (myScore.innovation * 0.2 + myScore.technical_implementation * 0.25 + 
                             myScore.problem_relevance * 0.25 + myScore.scalability_impact * 0.15 + 
                             myScore.presentation_quality * 0.15) * 10
                          ).toFixed(1) : 'N/A';
                          
                          return (
                            <tr key={submission.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium">{submission.team_name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{submission.project_title}</td>
                              <td className="px-4 py-3 text-center">
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                                  {myTotal}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {submission.ai_evaluation?.scores?.total_score ? (
                                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                                    {submission.ai_evaluation.scores.total_score}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => setViewingDetails(submission)}
                                  className="text-blue-600 hover:underline text-sm"
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Full Details Modal */}
      {viewingDetails && !selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg">{viewingDetails.team_name}</h3>
                <p className="text-sm text-gray-500">{viewingDetails.project_title}</p>
              </div>
              <button onClick={() => setViewingDetails(null)} className="text-gray-500 hover:text-gray-700 p-2">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Problem Statement */}
              {viewingDetails.problem_statement?.title && (
                <div className="bg-purple-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-purple-800 flex items-center gap-2 mb-2">
                    <Target size={18} />
                    Problem Statement
                  </h4>
                  <p className="font-semibold text-gray-900">{viewingDetails.problem_statement.title}</p>
                  {viewingDetails.problem_statement.description && (
                    <p className="text-sm text-gray-600 mt-2">{viewingDetails.problem_statement.description}</p>
                  )}
                </div>
              )}

              {/* Team Members */}
              <TeamMembersCard members={viewingDetails.team_members} />

              {/* Project Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-800 mb-3">Project Details</h4>
                <p className="text-sm text-gray-600 mb-3">{viewingDetails.project_description}</p>
                {viewingDetails.tech_stack?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {viewingDetails.tech_stack.map((tech, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Round Submissions */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-800 flex items-center gap-2 mb-3">
                  <Layers size={18} />
                  Round-wise Submissions
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <RoundSubmissionCard 
                    roundNumber={1} 
                    submission={viewingDetails.round1} 
                    isActive={viewingDetails.current_round >= 1}
                  />
                  <RoundSubmissionCard 
                    roundNumber={2} 
                    submission={viewingDetails.round2} 
                    isActive={viewingDetails.current_round >= 2}
                  />
                  <RoundSubmissionCard 
                    roundNumber={3} 
                    submission={viewingDetails.round3} 
                    isActive={viewingDetails.current_round >= 3}
                  />
                </div>
              </div>

              {/* AI Evaluation */}
              <AIScoreCard 
                aiEvaluation={viewingDetails.ai_evaluation} 
                onUseScores={null}
              />

              {/* Previous Scores */}
              {viewingDetails.all_jury_scores?.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-3">All Jury Scores</h4>
                  <div className="space-y-2">
                    {viewingDetails.all_jury_scores.map((score, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border flex justify-between items-center">
                        <div>
                          <span className="font-medium">{score.jury_name}</span>
                          <span className="text-xs text-gray-500 ml-2">Round {score.round}</span>
                        </div>
                        <span className="font-bold text-purple-600">
                          {((score.innovation * 0.2 + score.technical_implementation * 0.25 + 
                             score.problem_relevance * 0.25 + score.scalability_impact * 0.15 + 
                             score.presentation_quality * 0.15) * 10).toFixed(1)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setViewingDetails(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
              >
                Close
              </button>
              {!viewingDetails.scored_this_round && (
                <button
                  onClick={() => setSelectedSubmission(viewingDetails)}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Star size={18} />
                  Score This Submission
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Scoring Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg">Score Submission</h3>
                <p className="text-sm text-gray-500">{selectedSubmission.team_name} - {selectedSubmission.project_title}</p>
              </div>
              <button
                onClick={() => {setSelectedSubmission(null); resetScoreForm();}}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* AI Score Reference */}
              {selectedSubmission.ai_evaluation?.scores && (
                <AIScoreCard 
                  aiEvaluation={selectedSubmission.ai_evaluation}
                  onUseScores={() => applyAiScores(selectedSubmission.ai_evaluation.scores)}
                />
              )}

              {/* Score Sliders */}
              <div className="space-y-1">
                <ScoreSlider
                  label="Innovation"
                  weight="20%"
                  description="Novelty and creativity of the solution"
                  value={scoreForm.innovation}
                  onChange={(v) => setScoreForm({ ...scoreForm, innovation: v })}
                />
                <ScoreSlider
                  label="Technical Implementation"
                  weight="25%"
                  description="Code quality, architecture, and technical choices"
                  value={scoreForm.technical_implementation}
                  onChange={(v) => setScoreForm({ ...scoreForm, technical_implementation: v })}
                />
                <ScoreSlider
                  label="Problem Relevance"
                  weight="25%"
                  description="How well the project addresses the problem statement"
                  value={scoreForm.problem_relevance}
                  onChange={(v) => setScoreForm({ ...scoreForm, problem_relevance: v })}
                />
                <ScoreSlider
                  label="Scalability & Impact"
                  weight="15%"
                  description="Potential for real-world impact and growth"
                  value={scoreForm.scalability_impact}
                  onChange={(v) => setScoreForm({ ...scoreForm, scalability_impact: v })}
                />
                <ScoreSlider
                  label="Presentation Quality"
                  weight="15%"
                  description="Documentation, demo quality, and clarity"
                  value={scoreForm.presentation_quality}
                  onChange={(v) => setScoreForm({ ...scoreForm, presentation_quality: v })}
                />
              </div>

              {/* Total Score */}
              <div className="bg-purple-50 rounded-lg p-4 my-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-purple-800">Weighted Total Score</span>
                  <span className="text-3xl font-bold text-purple-600">{calculateTotalScore()}/100</span>
                </div>
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium mb-2">Comments & Feedback</label>
                <textarea
                  value={scoreForm.comments}
                  onChange={(e) => setScoreForm({ ...scoreForm, comments: e.target.value })}
                  className="w-full border rounded-lg px-4 py-3 h-24 resize-none"
                  placeholder="Add your feedback for the team (optional)"
                />
              </div>
            </div>

            <div className="border-t px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {setSelectedSubmission(null); resetScoreForm();}}
                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={submitScore}
                disabled={scoring}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="submit-score-btn"
              >
                {scoring ? <Loader className="animate-spin" size={18} /> : <Send size={18} />}
                {scoring ? 'Submitting...' : 'Submit Score'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JuryDashboardPage;
